/**
 * 全局信息类埋点（H5_OPEN / PAGE_EXPOSURE / PAGE_DATA_LOADED / USER_REFERER）
 *
 * @description
 * - H5_OPEN：useGlobalTracker（首次访问 + 回前台首次交互）
 * - PAGE_EXPOSURE：GlobalTrackerSync（pathname 变更）
 * - PAGE_DATA_LOADED：fetch 首次有效请求成功（整页生命周期一次；DCL 耗时来自 performance-log/loading-timing）
 * - USER_REFERER：GlobalTrackerSync（localStorage 设备维度仅一次）
 * - DYEING_DID：useLandingData（localStorage 设备维度仅一次）
 *
 * 字段经 trackCustom 合并 current_page / referrer_page 等页面上下文后上报（业务埋点，非 performance-log）
 */

import {
    SESSION_KEY_FIREBASE_LOGIN_METHOD,
    STORAGE_KEY_DYEING_DID_REPORTED,
    STORAGE_KEY_USER_REFERER_REPORTED,
} from '@/constants/storage';
import { getDocumentDclDurationMs, getPageLoadElapsedMs } from '@/utils/performance-log/loading-timing';
import { localStore, STORAGE_FLAG_ON } from '@/utils/storage';

import { trackCustom } from '../index';
import { ETrackerEvents } from '../tracker-events';

/** 阅读器相关 query：曝光 / 数据加载完成埋点使用 */
const READER_QUERY_KEYS = ['book_id', 'chapter_id', 'drama_id'] as const;

/** 首次访问 URL query 可解析的落地页 / 归因字段（H5_OPEN 使用） */
const LANDING_QUERY_KEYS = [
    'book_id',
    'chapter_id',
    'web_sign',
    'fbc',
    'fbp',
    'pixel_id',
    'nonce',
    'track_id',
    'ttclid',
    'ttp',
    'lp_click_id',
    'channel_id',
    'user_ip',
    'ad_id',
    'campaign_id',
    'adset_id',
    'drama_id',
] as const;

/**
 * 从当前页 URL query 解析指定字段（有值才写入）
 * @param keys 需要提取的 query 名列表
 * @returns 至少有一个有效字段时返回对象，否则 undefined（trackCustom 不传空对象）
 */
function resolveReaderQueryFromUrl(keys: readonly string[]): Record<string, string> | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const search = new URLSearchParams(window.location.search);
    const result: Record<string, string> = {};
    keys.forEach(key => {
        const value = search.get(key);
        if (value != null && value !== '') {
            result[key] = value;
        }
    });
    // 短剧落地仅有 drama_id 时，与阅读器 book_id 口径对齐
    if (result.drama_id && !result.book_id) {
        result.book_id = result.drama_id;
    }
    return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * 页面打开埋点（h5_open）
 * @description 携带 landing 归因与登录态；由 useGlobalTracker 在首次访问或回前台首次交互时调用
 */
export function logH5PageOpen(isLogged: boolean) {
    const landingParams = resolveReaderQueryFromUrl(LANDING_QUERY_KEYS);
    const loginMethod = localStore.getItem(SESSION_KEY_FIREBASE_LOGIN_METHOD) ?? '';
    const params: Record<string, unknown> = landingParams ? { ...landingParams } : {};

    params.login_method = loginMethod;
    params.login_status = isLogged ? 1 : 0;
    trackCustom(ETrackerEvents.H5_OPEN, params);
}

/**
 * 页面曝光埋点（page_exposure）
 * @description 路由 pathname 变更时上报；仅带阅读器 query，不含完整 landing 归因集
 */
export function logPageExposureTrack(): void {
    trackCustom(ETrackerEvents.PAGE_EXPOSURE, resolveReaderQueryFromUrl(READER_QUERY_KEYS));
}

/**
 * 页面数据加载完成埋点（page_data_loaded）
 * @description 整页首次有效 API 成功后上报一次；耗时见 @/utils/performance-log/loading-timing
 */
export function logPageDataLoadedTrack(): void {
    const params: Record<string, unknown> = { ...(resolveReaderQueryFromUrl(READER_QUERY_KEYS) ?? {}) };
    params.static_load_duration = getDocumentDclDurationMs();
    params.load_duration = getPageLoadElapsedMs();
    trackCustom(ETrackerEvents.PAGE_DATA_LOADED, params);
}

/**
 * 用户来源 referer 埋点（user_referer）
 * @description 设备维度仅上报一次；由 GlobalTrackerSync 在根布局挂载时调用
 */
export function logUserRefererTrack(): void {
    if (localStore.getNumberFlag(STORAGE_KEY_USER_REFERER_REPORTED) === STORAGE_FLAG_ON) {
        return;
    }

    localStore.setNumberFlag(STORAGE_KEY_USER_REFERER_REPORTED, STORAGE_FLAG_ON);

    const userReferer = typeof document !== 'undefined' ? (document.referrer?.trim() ?? '') : '';
    trackCustom(ETrackerEvents.USER_REFERER, { bef_url: userReferer });
}

/**
 * 落地页染色埋点（dyeing_did）
 * @param flag 0：不满足染色条件，1：满足染色条件
 * @description 设备维度仅上报一次；由 useLandingData 在染色条件满足或不满足时调用
 */
export function logDyeingDidTrack(flag: number): void {
    if (localStore.getNumberFlag(STORAGE_KEY_DYEING_DID_REPORTED) === STORAGE_FLAG_ON) {
        return;
    }

    localStore.setNumberFlag(STORAGE_KEY_DYEING_DID_REPORTED, STORAGE_FLAG_ON);
    trackCustom(ETrackerEvents.DYEING_DID, { is_dyeing_meet: flag });
}
